import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
    GET_DEMAND_CYCLES,
    GET_DEMAND_CYCLE_BY_UUID,
    CREATE_DEMAND_CYCLE,
    UPDATE_DEMAND_CYCLE,
    EXTEND_DEMAND_CYCLE_WINDOW,
    ACTIVATE_DEMAND_CYCLE,
    CANCEL_DEMAND_CYCLE,
    CLOSE_DEMAND_CYCLE,
    DELETE_DEMAND_CYCLE,
} from '@modules/demand-requirements/demand-cycles.graphql';
import {
    GetDemandCyclesResponse,
    GetDemandCycleByUuidResponse,
    CreateDemandCycleResponse,
    UpdateDemandCycleResponse,
    ExtendDemandCycleWindowResponse,
    ActivateDemandCycleResponse,
    CancelDemandCycleResponse,
    CloseDemandCycleResponse,
    DeleteDemandCycleResponse,
} from '@modules/demand-requirements/types/demand-cycle';
import { DemandCyclesActions } from './demand-cycles.actions';
import { Router } from '@angular/router';

@Injectable()
export class DemandCyclesEffects {
    private readonly actions$ = inject(Actions);
    private readonly graphqlService = inject(GraphQlService);
    private readonly swalService = inject(SwalService);
    private readonly router = inject(Router);

    loadAll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.loadAll),
            switchMap(({ pageableParam }) =>
                this.graphqlService
                    .query<GetDemandCyclesResponse>({
                        query: GET_DEMAND_CYCLES,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: pageableParam,
                    })
                    .pipe(
                        map((response) =>
                            DemandCyclesActions.loadAllSuccess({
                                data: response.data?.demandCycles?.content ?? [],
                                totalElements: response.data?.demandCycles?.totalElements ?? 0,
                            })
                        ),
                        catchError((error) =>
                            of(DemandCyclesActions.loadAllFailure({ error: error?.message ?? 'Failed to load demand windows' }))
                        )
                    )
            )
        )
    );

    loadByUuid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.loadByUuid),
            switchMap(({ uuid }) =>
                this.graphqlService
                    .query<GetDemandCycleByUuidResponse>({
                        query: GET_DEMAND_CYCLE_BY_UUID,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { uuid },
                    })
                    .pipe(
                        map((response) => {
                            const data = response.data?.demandCycleByUuid?.data;
                            if (data) {
                                return DemandCyclesActions.loadByUuidSuccess({ entity: data });
                            }
                            return DemandCyclesActions.loadByUuidFailure({ error: response.data?.demandCycleByUuid?.message ?? 'Not found' });
                        }),
                        catchError((error) =>
                            of(DemandCyclesActions.loadByUuidFailure({ error: error?.message ?? 'Failed to load demand window' }))
                        )
                    )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.create),
            mergeMap(({ input }) =>
                this.graphqlService
                    .mutate<CreateDemandCycleResponse>({
                        mutation: CREATE_DEMAND_CYCLE,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { input },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.createDemandCycle;
                            if (this.isSuccess(res?.status) && res?.data) {
                                this.swalService.success(res.message ?? 'Demand window created successfully');
                                this.router.navigate(['/mis/demand-requirements/demand-cycles']);
                                return DemandCyclesActions.createSuccess({ entity: res.data });
                            }
                            return DemandCyclesActions.createFailure({ error: res?.message ?? 'Failed to create demand window' });
                        }),
                        catchError((error) =>
                            of(DemandCyclesActions.createFailure({ error: error?.message ?? 'Failed to create demand window' }))
                        )
                    )
            )
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.update),
            mergeMap(({ uuid, input }) =>
                this.graphqlService
                    .mutate<UpdateDemandCycleResponse>({
                        mutation: UPDATE_DEMAND_CYCLE,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { uuid, input },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.updateDemandCycle;
                            if (this.isSuccess(res?.status) && res?.data) {
                                this.swalService.success(res.message ?? 'Demand window updated successfully');
                                this.router.navigate(['/mis/demand-requirements/demand-cycles', uuid]);
                                return DemandCyclesActions.updateSuccess({ entity: res.data });
                            }
                            return DemandCyclesActions.updateFailure({ error: res?.message ?? 'Failed to update demand window' });
                        }),
                        catchError((error) =>
                            of(DemandCyclesActions.updateFailure({ error: error?.message ?? 'Failed to update demand window' }))
                        )
                    )
            )
        )
    );

    extendWindow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.extendWindow),
            mergeMap(({ uuid, input }) =>
                this.graphqlService
                    .mutate<ExtendDemandCycleWindowResponse>({
                        mutation: EXTEND_DEMAND_CYCLE_WINDOW,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { uuid, input },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.extendDemandCycleWindow;
                            if (this.isSuccess(res?.status) && res?.data) {
                                this.swalService.success(res.message ?? 'Demand window extension requested successfully');
                                return DemandCyclesActions.extendWindowSuccess({ entity: res.data });
                            }
                            return DemandCyclesActions.extendWindowFailure({ error: res?.message ?? 'Failed to extend demand window' });
                        }),
                        catchError((error) =>
                            of(DemandCyclesActions.extendWindowFailure({ error: error?.message ?? 'Failed to extend demand window' }))
                        )
                    )
            )
        )
    );

    activate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.activate),
            mergeMap(({ uuid }) =>
                this.graphqlService
                    .mutate<ActivateDemandCycleResponse>({
                        mutation: ACTIVATE_DEMAND_CYCLE,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { uuid },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.activateDemandCycle;
                            if (this.isSuccess(res?.status) && res?.data) {
                                this.swalService.success(res.message ?? 'Demand window activated successfully');
                                return DemandCyclesActions.activateSuccess({ entity: res.data });
                            }
                            return DemandCyclesActions.activateFailure({ error: res?.message ?? 'Failed to activate demand window' });
                        }),
                        catchError((error) =>
                            of(DemandCyclesActions.activateFailure({ error: error?.message ?? 'Failed to activate demand window' }))
                        )
                    )
            )
        )
    );

    cancel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.cancel),
            mergeMap(({ uuid, reason }) =>
                this.graphqlService
                    .mutate<CancelDemandCycleResponse>({
                        mutation: CANCEL_DEMAND_CYCLE,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { uuid, reason },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.cancelDemandCycle;
                            if (this.isSuccess(res?.status) && res?.data) {
                                this.swalService.success(res.message ?? 'Demand window cancelled successfully');
                                return DemandCyclesActions.cancelSuccess({ entity: res.data });
                            }
                            return DemandCyclesActions.cancelFailure({ error: res?.message ?? 'Failed to cancel demand window' });
                        }),
                        catchError((error) =>
                            of(DemandCyclesActions.cancelFailure({ error: error?.message ?? 'Failed to cancel demand window' }))
                        )
                    )
            )
        )
    );

    close$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.close),
            mergeMap(({ uuid }) =>
                this.graphqlService
                    .mutate<CloseDemandCycleResponse>({
                        mutation: CLOSE_DEMAND_CYCLE,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { uuid },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.closeDemandCycle;
                            if (this.isSuccess(res?.status) && res?.data) {
                                this.swalService.success(res.message ?? 'Demand window closed successfully');
                                return DemandCyclesActions.closeSuccess({ entity: res.data });
                            }
                            return DemandCyclesActions.closeFailure({ error: res?.message ?? 'Failed to close demand window' });
                        }),
                        catchError((error) =>
                            of(DemandCyclesActions.closeFailure({ error: error?.message ?? 'Failed to close demand window' }))
                        )
                    )
            )
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DemandCyclesActions.delete),
            mergeMap(({ uuid }) =>
                this.graphqlService
                    .mutate<DeleteDemandCycleResponse>({
                        mutation: DELETE_DEMAND_CYCLE,
                        apolloNamespace: ApolloNamespace.registration,
                        variables: { uuid },
                    })
                    .pipe(
                        map((response) => {
                            const res = response.data?.deleteDemandCycle;
                            if (this.isSuccess(res?.status)) {
                                this.swalService.success(res.message ?? 'Demand window deleted successfully');
                                return DemandCyclesActions.deleteSuccess({ uuid });
                            }
                            return DemandCyclesActions.deleteFailure({ error: res?.message ?? 'Failed to delete demand window' });
                        }),
                        catchError((error) =>
                            of(DemandCyclesActions.deleteFailure({ error: error?.message ?? 'Failed to delete demand window' }))
                        )
                    )
            )
        )
    );

    actionFailureNotifications$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    DemandCyclesActions.extendWindowFailure,
                    DemandCyclesActions.activateFailure,
                    DemandCyclesActions.cancelFailure,
                    DemandCyclesActions.closeFailure,
                    DemandCyclesActions.deleteFailure,
                ),
                tap(({ error }) => {
                    this.swalService.error(this.errorMessage(error, 'Demand window operation failed'));
                }),
            ),
        { dispatch: false },
    );

    private isSuccess(status: unknown): boolean {
        return status === true;
    }

    private errorMessage(error: unknown, fallback: string): string {
        if (typeof error === 'string' && error.trim()) {
            return error;
        }

        if (error && typeof error === 'object' && 'message' in error) {
            const message = String((error as { message?: unknown }).message ?? '').trim();
            return message || fallback;
        }

        return fallback;
    }
}
